// Every public page, with the title and description search engines show.
//
// This is the ONE source of truth for both the per-page <title>/description
// and the sitemap (scripts/generate-sitemap.js reads this same file), so a
// new page cannot end up in the sitemap without a title, or carry a title
// nobody can find.
//
// Before this existed every route served the identical
// "<title>StarView Cinema</title>" and one description, because the head is
// baked once into public/index.html and react-router never touched it. To a
// crawler that is ~30 URLs of duplicate metadata, which is how a site ends
// up indexed as a single page no matter how many it has.
//
// `priority` and `changefreq` are sitemap hints only.
export const SITE_URL = 'https://starviewcinema.com';
export const SITE_NAME = 'StarView Cinema';

export const PUBLIC_ROUTES = [
  {
    path: '/coming-soon',
    title: 'Trending Now | StarView Cinema',
    description: 'The films audiences across 199 StarView markets are watching most right now, with trailers and instant tickets.',
    priority: 0.8,
    changefreq: 'daily',
  },
  {
    path: '/starview-x',
    title: 'StarView X Membership | StarView Cinema',
    description: 'A free movie ticket every month, bonus Galaxies, member pricing and early premiere access - StarView X works in every StarView market.',
    priority: 0.8,
    changefreq: 'monthly',
  },
  {
    path: '/watch-parties',
    title: 'Watch Parties | StarView Cinema',
    description: 'Host a synchronized private screening with friends in any of 199 countries - your own premiere night, second-perfect for every guest.',
    priority: 0.7,
    changefreq: 'monthly',
  },
  {
    path: '/get-the-app',
    title: 'Get the StarView App',
    description: 'Theater rooms, Galaxies and loyalty rewards, Blast Pass, OrderUp food delivery and watch parties - the full StarView Cinema experience on iOS and Android.',
    priority: 0.8,
    changefreq: 'monthly',
  },
  {
    path: '/',
    title: 'StarView Cinema | Watch New Releases in 200+ Countries',
    description:
      'StarView Cinema streams new-release films in over 200 countries, with premium formats, real showtimes, gift cards and StarView X membership. Watch from anywhere.',
    priority: 1.0,
    changefreq: 'daily',
  },
  {
    path: '/Showtimes',
    title: 'Showtimes & Now Playing | StarView Cinema',
    description:
      'See what is playing now at StarView Cinema and book a seat. Real showtimes for every film, in your country and your language.',
    priority: 0.9,
    changefreq: 'daily',
  },
  {
    path: '/theatres',
    title: 'Our Theatres | StarView Cinema',
    description:
      'Explore StarView Cinema auditoriums and premium formats, from standard screens to our large-format and immersive theatres.',
    priority: 0.8,
    changefreq: 'weekly',
  },
  {
    path: '/GiftCards',
    title: 'Cinema Gift Cards | StarView Cinema',
    description:
      'Buy a StarView Cinema gift card for films, snacks and StarView X membership. Delivered instantly and redeemable in 200+ countries.',
    priority: 0.8,
    changefreq: 'weekly',
  },
  {
    path: '/Offers',
    title: 'Offers & Deals | StarView Cinema',
    description:
      'Current StarView Cinema offers: ticket deals, points multipliers, Blast Pass rewards and member-only pricing.',
    priority: 0.8,
    changefreq: 'daily',
  },
  {
    path: '/CountryExclusive',
    title: 'Country Exclusives | StarView Cinema',
    description:
      'Films and offers available only in your country. StarView Cinema curates a different line-up for each of the 200+ markets it serves.',
    priority: 0.8,
    changefreq: 'daily',
  },
  {
    path: '/Merchandise',
    title: 'Cinema Merchandise | StarView Cinema',
    description: 'Official StarView Cinema merchandise, collectibles and film memorabilia.',
    priority: 0.6,
    changefreq: 'weekly',
  },
  {
    path: '/our-brands',
    title: 'Our Brands | StarView Cinema',
    description:
      'The StarView family: StarView Cinema, StarView X membership, OrderUp delivery and StarView Party.',
    priority: 0.6,
    changefreq: 'monthly',
  },
  {
    path: '/ratings-information',
    title: 'Film Ratings Explained | StarView Cinema',
    description:
      'What each film rating means and how StarView Cinema applies age ratings across different countries.',
    priority: 0.5,
    changefreq: 'monthly',
  },
  {
    path: '/faqs',
    title: 'Frequently Asked Questions | StarView Cinema',
    description:
      'Answers about tickets, refunds, StarView X membership, Galaxies, points and watching in your country.',
    priority: 0.6,
    changefreq: 'monthly',
  },
  {
    path: '/contact-us',
    title: 'Contact Us | StarView Cinema',
    description: 'Get in touch with StarView Cinema support, press, partnerships or corporate teams.',
    priority: 0.5,
    changefreq: 'monthly',
  },
  {
    path: '/careers',
    title: 'Careers | StarView Cinema',
    description: 'Open roles at StarView Cinema and what it is like to build cinema for 200+ countries.',
    priority: 0.6,
    changefreq: 'weekly',
  },
  {
    path: '/drive',
    title: 'Drive with OrderUp | StarView Cinema',
    description:
      'Deliver food and cinema orders with OrderUp. Apply to drive, set your own hours and get paid per delivery.',
    priority: 0.7,
    changefreq: 'weekly',
  },
  {
    path: '/partner/restaurant',
    title: 'Restaurant Partners | OrderUp by StarView',
    description:
      'Put your restaurant in front of StarView Cinema audiences. Partner with OrderUp and start taking orders.',
    priority: 0.7,
    changefreq: 'weekly',
  },
  {
    path: '/partner/restaurant/apply',
    title: 'Apply as a Restaurant Partner | OrderUp by StarView',
    description: 'Apply to list your restaurant on OrderUp and reach StarView Cinema customers in your city.',
    priority: 0.6,
    changefreq: 'monthly',
  },
  {
    path: '/corporate-information',
    title: 'Corporate Information | StarView Cinema',
    description: 'StarView Cinema company information, governance and corporate disclosures.',
    priority: 0.4,
    changefreq: 'monthly',
  },
  {
    path: '/investor-relations',
    title: 'Investor Relations | StarView Cinema',
    description: 'StarView Cinema investor relations, financial reporting and shareholder information.',
    priority: 0.5,
    changefreq: 'monthly',
  },
  {
    path: '/investor-connect',
    title: 'Investor Connect | StarView Cinema',
    description: 'Connect with StarView Cinema investor relations.',
    priority: 0.4,
    changefreq: 'monthly',
  },
  {
    path: '/media-center',
    title: 'Media Center & Press | StarView Cinema',
    description: 'StarView Cinema press releases, brand assets and media enquiries.',
    priority: 0.5,
    changefreq: 'weekly',
  },
  {
    path: '/privacy-policy',
    title: 'Privacy Policy | StarView Cinema',
    description: 'How StarView Cinema collects, uses and protects your personal information.',
    priority: 0.3,
    changefreq: 'yearly',
  },
  {
    path: '/terms-and-conditions',
    title: 'Terms and Conditions | StarView Cinema',
    description: 'The terms governing use of StarView Cinema websites, apps and services.',
    priority: 0.3,
    changefreq: 'yearly',
  },
  {
    path: '/starview-cinema-terms-and-conditions',
    title: 'StarView Cinema Service Terms | StarView Cinema',
    description: 'Service-specific terms for StarView Cinema tickets, membership and rewards.',
    priority: 0.3,
    changefreq: 'yearly',
  },
  {
    path: '/california-collection-notice',
    title: 'California Collection Notice | StarView Cinema',
    description: 'Notice at collection for California residents under the CCPA/CPRA.',
    priority: 0.3,
    changefreq: 'yearly',
  },
  {
    path: '/vulnerability-reporting',
    title: 'Report a Vulnerability | StarView Cinema',
    description: 'How to responsibly disclose a security vulnerability to StarView Cinema.',
    priority: 0.3,
    changefreq: 'yearly',
  },
];

// Deliberately absent from the sitemap, and marked noindex by RouteMeta:
// authenticated, transactional or staff-only pages. Putting /admin or
// /Login in a sitemap invites crawling of pages that can only ever return a
// gate, which wastes crawl budget and looks like thin content.
export const PRIVATE_PATH_PREFIXES = [
  '/admin',
  '/Login',
  '/Success',
  '/Cashflow',
  '/TheaterRoom',
  '/associate-portal',
];

export function isPrivatePath(pathname) {
  const p = String(pathname || '');
  return PRIVATE_PATH_PREFIXES.some(
    (prefix) => p.toLowerCase() === prefix.toLowerCase() || p.toLowerCase().startsWith(`${prefix.toLowerCase()}/`)
  );
}

export function metaForPath(pathname) {
  const p = String(pathname || '/');
  // '/home' renders the same component as '/', so it gets the same metadata
  // and canonicals to '/' - two URLs serving one page is a duplicate-content
  // signal otherwise.
  const normalized = p.toLowerCase() === '/home' ? '/' : p;
  return (
    PUBLIC_ROUTES.find((r) => r.path.toLowerCase() === normalized.toLowerCase()) || null
  );
}
